import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { api } from "@/lib/site";

const empty = { name: "", company: "", email: "", message: "" };

export default function CI62DemoForm() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        service: "CI62 X-Rite Spectrophotometer — Demo Request",
        message: form.message.trim(),
      });
      toast.success("Demo request received. Our team will contact you shortly.");
      setForm(empty);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-slate-300 text-slate-900 px-4 h-12 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 transition-colors placeholder:text-slate-400";

  return (
    <form data-testid="ci62-demo-form" onSubmit={onSubmit} noValidate className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-slate-600 mb-2 font-medium">
            Name <span className="text-teal-700">*</span>
          </label>
          <input
            data-testid="ci62-demo-name"
            value={form.name}
            onChange={setField("name")}
            placeholder="Full name"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-slate-600 mb-2 font-medium">
            Company
          </label>
          <input
            data-testid="ci62-demo-company"
            value={form.company}
            onChange={setField("company")}
            placeholder="Company name"
            className={inputCls}
          />
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-slate-600 mb-2 font-medium">
          Email <span className="text-teal-700">*</span>
        </label>
        <input
          data-testid="ci62-demo-email"
          type="email"
          value={form.email}
          onChange={setField("email")}
          placeholder="you@company.com"
          className={inputCls}
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-slate-600 mb-2 font-medium">
          Message <span className="text-teal-700">*</span>
        </label>
        <textarea
          data-testid="ci62-demo-message"
          value={form.message}
          onChange={setField("message")}
          rows={4}
          placeholder="Tell us about your application, sample types and current color QC process..."
          className="w-full bg-white border border-slate-300 text-slate-900 px-4 py-3 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 transition-colors resize-none placeholder:text-slate-400"
        />
      </div>
      <button
        type="submit"
        data-testid="ci62-demo-submit"
        disabled={submitting}
        className="inline-flex items-center justify-center bg-teal-700 hover:bg-teal-800 text-white px-8 h-12 text-sm font-medium tracking-wide transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 w-4 h-4 animate-spin" strokeWidth={2} />
            Sending...
          </>
        ) : (
          <>
            Request a Demo
            <Send className="ml-2 w-4 h-4" strokeWidth={2} />
          </>
        )}
      </button>
    </form>
  );
}
